// The sub-album hierarchy of a new album, as edited in the creation dialog, and the folder
// structure it becomes:
//   root album:  "<YYYYMMDD>_<slug>", info.json with the title and event tags
//   mid-level:   "<slug>", info.json with the title only
//   leaf album:  "<slug>", info.json with the title and photographers, plus the web/archive/plazma bins
// A root without sub-albums is itself a leaf, so it gets every tag and the bins.
// An event series ("rendezvénysorozat", e.g. G7, Qpa) is only the dated root folder with its info.json
// (title and event tags): a collection for albums that have dates of their own, added later.
// Without a date (a sub-album added to an existing album), the root folder is named "<slug>" too.

import {EventInfo} from './eventInfo';
import {formatEventFolderName, slugify} from './naming';

export interface AlbumTreeNode {
    /** Stable key for editing; never saved. */
    id: string;
    name: string;
    children: AlbumTreeNode[];
}

let nextNodeId = 0;

export function createAlbumNode(name = ''): AlbumTreeNode {
    nextNodeId += 1;
    return {id: `album-${nextNodeId}`, name, children: []};
}

/** Applies `update` to the node with the given id, copying only the nodes on its path. */
function mapNode(tree: AlbumTreeNode[], id: string, update: (node: AlbumTreeNode) => AlbumTreeNode): AlbumTreeNode[] {
    let changed = false;
    const result = tree.map((node) => {
        if (node.id === id) {
            changed = true;
            return update(node);
        }
        const children = mapNode(node.children, id, update);
        if (children === node.children) return node;
        changed = true;
        return {...node, children};
    });
    return changed ? result : tree;
}

/** Adds `child` as the last child of `parentId`, or of the root album when it is `null`. */
export function addAlbumChild(tree: AlbumTreeNode[], parentId: string | null, child: AlbumTreeNode): AlbumTreeNode[] {
    if (parentId === null) return [...tree, child];
    return mapNode(tree, parentId, (node) => ({...node, children: [...node.children, child]}));
}

export function renameAlbumNode(tree: AlbumTreeNode[], id: string, name: string): AlbumTreeNode[] {
    return mapNode(tree, id, (node) => ({...node, name}));
}

/** Removes the node together with its sub-albums. */
export function removeAlbumNode(tree: AlbumTreeNode[], id: string): AlbumTreeNode[] {
    let changed = false;
    const result: AlbumTreeNode[] = [];
    for (const node of tree) {
        if (node.id === id) {
            changed = true;
            continue;
        }
        const children = removeAlbumNode(node.children, id);
        if (children !== node.children) changed = true;
        result.push(children === node.children ? node : {...node, children});
    }
    return changed ? result : tree;
}

/** Why the sub-album tree can't be created as it is, or `null` if it can. */
export function albumTreeProblem(tree: AlbumTreeNode[]): string | null {
    const folderNames = new Set<string>();
    for (const node of tree) {
        if (!node.name.trim()) return 'Minden alalbumnak adj nevet!';
        const folderName = slugify(node.name);
        if (folderNames.has(folderName)) {
            return `Két alalbum is ugyanazt a mappanevet kapná egy szinten: „${folderName}”.`;
        }
        folderNames.add(folderName);
    }
    for (const node of tree) {
        const problem = albumTreeProblem(node.children);
        if (problem) return problem;
    }
    return null;
}

/** One folder to create, with the content of its info.json. */
export interface AlbumPlan {
    folderName: string;
    info: EventInfo;
    /** Whether the web/archive/plazma bins go into it (leaf albums only). */
    bins: boolean;
    children: AlbumPlan[];
}

export interface AlbumTreeInput {
    /** The root folder's date prefix; `null` for none. */
    date: Date | null;
    title: string;
    photographers: string[];
    events: string[];
    children: AlbumTreeNode[];
    /** An event series: no bins, photographers or sub-albums, whatever the other fields say. */
    series?: boolean;
}

function planSubAlbum(node: AlbumTreeNode, photographers: string[]): AlbumPlan {
    const title = node.name.trim();
    const isLeaf = node.children.length === 0;
    return {
        folderName: slugify(title),
        info: {title, photographers: isLeaf ? photographers : [], events: []},
        bins: isLeaf,
        children: node.children.map((child) => planSubAlbum(child, photographers)),
    };
}

/** The folders and info.json files the album and its sub-albums are created as. */
export function planAlbumTree({date, title, photographers, events, children, series = false}: AlbumTreeInput): AlbumPlan {
    const rootTitle = title.trim();
    const folderName = date ? formatEventFolderName(date, rootTitle) : slugify(rootTitle);
    if (series) return {folderName, info: {title: rootTitle, photographers: [], events}, bins: false, children: []};
    const isLeaf = children.length === 0;
    return {
        folderName,
        info: {title: rootTitle, photographers: isLeaf ? photographers : [], events},
        bins: isLeaf,
        children: children.map((child) => planSubAlbum(child, photographers)),
    };
}
